/**
 * use-haptic.js — Boothly haptic feedback hook
 *
 * Wraps navigator.vibrate() with semantic presets that map to
 * standard iOS HIG feedback intensities via vibration durations.
 *
 * Respects per-user haptic preference (localStorage 'boothly_haptic').
 * Default: enabled. Set to '0' to disable.
 *
 * Usage:
 *   const haptic = useHaptic();
 *   haptic.light()    // selection feedback (6ms)
 *   haptic.medium()   // button tap (10ms)
 *   haptic.heavy()    // confirm/save (15ms)
 *   haptic.success()  // completion (20ms)
 *   haptic.error()    // [10, 80, 10] double-pulse
 *   haptic.ms(30)     // raw duration for special cases
 */

const HAPTIC_KEY = 'boothly_haptic'

function isEnabled() {
  return localStorage.getItem(HAPTIC_KEY) !== '0'
}

const vibrate = (pattern) => {
  if (!isEnabled()) return
  try { navigator.vibrate?.(pattern); } catch {}
};

const PRESETS = {
  light:   () => vibrate(6),
  medium:  () => vibrate(10),
  heavy:   () => vibrate(15),
  success: () => vibrate(20),
  error:   () => vibrate([10, 80, 10]),
  ms:      (ms) => vibrate(ms),
};

/**
 * Returns stable haptic feedback helpers.
 * The returned object is referentially stable across renders.
 */
export function useHaptic() {
  return PRESETS;
}

/**
 * Direct haptic function for use outside of React components
 * (e.g. event handlers in plain JS files).
 */
export const haptic = (ms = 10) => vibrate(ms);

/** Read haptic enabled state (for UI toggle) */
export function getHapticEnabled() {
  return localStorage.getItem(HAPTIC_KEY) !== '0'
}

/** Write haptic enabled state (for UI toggle) */
export function setHapticEnabled(on) {
  localStorage.setItem(HAPTIC_KEY, on ? '1' : '0')
}
